import {getLegendBorderRects} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramExportLayoutHelper";

const RGBA_CHANNELS = 4
const DEBUG_TILE_BORDER_RGBA = [255, 0, 0, 255]
const LEGEND_BORDER_RGBA = [128, 128, 128, 255]

export default class GenogramCompositeRowReader {

	constructor(options = {}) {
		this.logger = options.logger || null
		this.finalWidth = options.finalWidth
		this.finalHeight = options.finalHeight
		this.genogramWidth = options.genogramWidth
		this.genogramHeight = options.genogramHeight
		this.sourceGenogramWidth = options.sourceGenogramWidth || this.genogramWidth
		this.sourceGenogramHeight = options.sourceGenogramHeight || this.genogramHeight
		this.padding = options.padding
		this.legendLeft = options.legendLeft
		this.legendTop = options.legendTop
		this.stripeHeight = options.stripeHeight
		this.plan = options.plan
		this.tiles = options.tiles || []
		this.tileDecoder = options.tileDecoder
		this.legendImage = options.legendImage
		this.watermarkPattern = options.watermarkPattern
		this.showWatermark = !!options.showWatermark
		this.showDebugTileBorders = !!options.showDebugTileBorders
		this.currentStripe = null
		this.currentTileCache = new Map()
	}

	async getRow(rowIndex) {
		await this.ensureStripe(rowIndex)
		let rowOffset = rowIndex - this.currentStripe.startRow
		let start = rowOffset * this.finalWidth * RGBA_CHANNELS
		let end = start + this.finalWidth * RGBA_CHANNELS
		return this.currentStripe.buffer.subarray(start, end)
	}

	async finish() {
		this.currentStripe = null
		this.currentTileCache.clear()
	}

	async ensureStripe(rowIndex) {
		let shouldRebuildStripe = this.currentStripe == null || rowIndex < this.currentStripe.startRow || rowIndex >= this.currentStripe.endRow

		if (shouldRebuildStripe) {
			await this.composeStripe(rowIndex)
		}
	}

	async composeStripe(rowIndex) {
		let startedAt = Date.now()
		let startRow = Math.floor(rowIndex / this.stripeHeight) * this.stripeHeight
		let endRow = Math.min(startRow + this.stripeHeight, this.finalHeight)
		let stripeHeight = endRow - startRow
		let buffer = this.createWhiteStripeBuffer(stripeHeight)
		await this.copyGenogramIntoStripe(buffer, startRow, stripeHeight)
		this.applyDebugTileBorders(buffer, startRow, stripeHeight)
		this.copyLegendIntoStripe(buffer, startRow, stripeHeight)
		this.applyLegendBorderIntoStripe(buffer, startRow, stripeHeight)
		this.applyWatermarkToStripe(buffer, startRow, stripeHeight)
		this.logger?.log("StripeComposed", {
			startRow,
			endRow,
			stripeHeight,
			stripeRawBytes: buffer.byteLength,
			cachedTiles: this.currentTileCache.size,
			durationMs: Date.now() - startedAt,
		})
		this.currentStripe = {
			startRow,
			endRow,
			buffer,
		}
	}

	createWhiteStripeBuffer(stripeHeight) {
		let buffer = new Uint8Array(this.finalWidth * stripeHeight * RGBA_CHANNELS)

		for (let offset = 0; offset < buffer.length; offset += RGBA_CHANNELS) {
			buffer[offset] = 255
			buffer[offset + 1] = 255
			buffer[offset + 2] = 255
			buffer[offset + 3] = 255
		}

		return buffer
	}

	async copyGenogramIntoStripe(buffer, startRow, stripeHeight) {
		if (this.isScaledGenogram()) {
			await this.copyScaledGenogramIntoStripe(buffer, startRow, stripeHeight)
			return
		}

		let stripeTopInGenogram = startRow - this.padding
		let stripeBottomInGenogram = stripeTopInGenogram + stripeHeight
		let relevantTiles = this.tiles.filter(tile => this.tileIntersectsStripe(tile, stripeTopInGenogram, stripeBottomInGenogram))
		await this.populateTileCache(relevantTiles)

		for (let tile of relevantTiles) {
			let tileData = this.currentTileCache.get(tile.index)
			this.copyTileIntoStripe(buffer, startRow, stripeHeight, tile, tileData)
		}
	}

	isScaledGenogram() {
		return this.sourceGenogramWidth !== this.genogramWidth || this.sourceGenogramHeight !== this.genogramHeight
	}

	async copyScaledGenogramIntoStripe(buffer, startRow, stripeHeight) {
		let sourceStripeBounds = this.getSourceStripeBounds(startRow, stripeHeight)
		let relevantTiles = this.tiles.filter(tile => this.tileIntersectsStripe(tile, sourceStripeBounds.top, sourceStripeBounds.bottom))
		await this.populateTileCache(relevantTiles)

		for (let tile of relevantTiles) {
			let tileData = this.currentTileCache.get(tile.index)
			this.copyScaledTileIntoStripe(buffer, startRow, stripeHeight, tile, tileData)
		}
	}

	getSourceStripeBounds(startRow, stripeHeight) {
		let destTop = Math.max(startRow - this.padding, 0)
		let destBottom = Math.min(startRow + stripeHeight - this.padding, this.genogramHeight)

		return {
			top: this.getSourceYFromDestY(destTop),
			bottom: this.getSourceYFromDestBottom(destBottom),
		}
	}

	tileIntersectsStripe(tile, stripeTopInGenogram, stripeBottomInGenogram) {
		let tileTop = this.getTileTop(tile)
		let tileBottom = tileTop + this.getTileHeight(tile)
		return tileBottom > stripeTopInGenogram && tileTop < stripeBottomInGenogram
	}

	async populateTileCache(relevantTiles) {
		let nextCache = new Map()
		let totalDecodedTileBytes = 0

		for (let tile of relevantTiles) {
			let tileData = this.currentTileCache.get(tile.index)
			if (!tileData) {
				tileData = await this.tileDecoder.decodeTileBlob(tile)
			}
			nextCache.set(tile.index, tileData)
			totalDecodedTileBytes += tileData?.data?.byteLength || 0
		}

		this.currentTileCache = nextCache
		this.logger?.log("StripeTileCachePrepared", {
			tileCount: relevantTiles.length,
			totalDecodedTileBytes,
			tileIndexes: relevantTiles.map(tile => tile.index),
		})
	}

	copyTileIntoStripe(buffer, startRow, stripeHeight, tile, tileData) {
		let tileLeft = this.getTileLeft(tile)
		let tileTop = this.getTileTop(tile)
		let tileWidth = Math.min(this.getTileWidth(tile), tileData.width)
		let tileHeight = Math.min(this.getTileHeight(tile), tileData.height)
		let stripeTopInGenogram = startRow - this.padding
		let overlapTop = Math.max(tileTop, stripeTopInGenogram)
		let overlapBottom = Math.min(tileTop + tileHeight, stripeTopInGenogram + stripeHeight)
		let copyHeight = Math.max(overlapBottom - overlapTop, 0)
		let sourceStartRow = overlapTop - tileTop
		let destStartRow = overlapTop - stripeTopInGenogram

		for (let rowOffset = 0; rowOffset < copyHeight; rowOffset++) {
			let sourceRow = sourceStartRow + rowOffset
			let destRow = destStartRow + rowOffset
			let sourceStart = (sourceRow * tileData.width + 0) * RGBA_CHANNELS
			let sourceEnd = sourceStart + tileWidth * RGBA_CHANNELS
			let destStart = ((destRow * this.finalWidth) + this.padding + tileLeft) * RGBA_CHANNELS
			buffer.set(tileData.data.subarray(sourceStart, sourceEnd), destStart)
		}
	}

	copyScaledTileIntoStripe(buffer, startRow, stripeHeight, tile, tileData) {
		let sourceTileLeft = this.getTileLeft(tile)
		let sourceTileTop = this.getTileTop(tile)
		let sourceTileWidth = Math.min(this.getTileWidth(tile), tileData.width)
		let sourceTileHeight = Math.min(this.getTileHeight(tile), tileData.height)
		let tileDestLeft = this.padding + this.getDestXStartFromSourceX(sourceTileLeft)
		let tileDestRight = this.padding + this.getDestXEndFromSourceX(sourceTileLeft + sourceTileWidth)
		let tileDestTop = this.padding + this.getDestYStartFromSourceY(sourceTileTop)
		let tileDestBottom = this.padding + this.getDestYEndFromSourceY(sourceTileTop + sourceTileHeight)
		let visibleTop = Math.max(tileDestTop, startRow)
		let visibleBottom = Math.min(tileDestBottom, startRow + stripeHeight)
		let visibleLeft = Math.max(tileDestLeft, this.padding)
		let visibleRight = Math.min(tileDestRight, this.padding + this.genogramWidth)

		if (visibleTop >= visibleBottom || visibleLeft >= visibleRight) {
			return
		}

		for (let y = visibleTop; y < visibleBottom; y++) {
			let destRowOffset = y - startRow
			let sourceY = this.getSourceYFromDestY(y - this.padding)
			let sourceRowInTile = sourceY - sourceTileTop

			if (sourceRowInTile < 0 || sourceRowInTile >= sourceTileHeight) {
				continue
			}

			for (let x = visibleLeft; x < visibleRight; x++) {
				let sourceX = this.getSourceXFromDestX(x - this.padding)
				let sourceColumnInTile = sourceX - sourceTileLeft

				if (sourceColumnInTile < 0 || sourceColumnInTile >= sourceTileWidth) {
					continue
				}

				let sourceOffset = ((sourceRowInTile * tileData.width) + sourceColumnInTile) * RGBA_CHANNELS
				let destOffset = ((destRowOffset * this.finalWidth) + x) * RGBA_CHANNELS
				buffer[destOffset] = tileData.data[sourceOffset]
				buffer[destOffset + 1] = tileData.data[sourceOffset + 1]
				buffer[destOffset + 2] = tileData.data[sourceOffset + 2]
				buffer[destOffset + 3] = tileData.data[sourceOffset + 3]
			}
		}
	}

	getTileLeft(tile) {
		return tile.renderedLeft ?? tile.left - this.plan.bounds.left
	}

	getTileTop(tile) {
		return tile.renderedTop ?? tile.top - this.plan.bounds.top
	}

	getTileWidth(tile) {
		return tile.renderedWidth ?? tile.width
	}

	getTileHeight(tile) {
		return tile.renderedHeight ?? tile.height
	}

	getSourceXFromDestX(destX) {
		return Math.min(
			this.sourceGenogramWidth - 1,
			Math.floor(destX * this.sourceGenogramWidth / this.genogramWidth)
		)
	}

	getSourceYFromDestY(destY) {
		return Math.min(
			this.sourceGenogramHeight - 1,
			Math.floor(destY * this.sourceGenogramHeight / this.genogramHeight)
		)
	}

	getSourceYFromDestBottom(destBottom) {
		return Math.min(
			this.sourceGenogramHeight,
			Math.ceil(destBottom * this.sourceGenogramHeight / this.genogramHeight)
		)
	}

	getDestXStartFromSourceX(sourceX) {
		return Math.floor(sourceX * this.genogramWidth / this.sourceGenogramWidth)
	}

	getDestXEndFromSourceX(sourceX) {
		return Math.ceil(sourceX * this.genogramWidth / this.sourceGenogramWidth)
	}

	getDestYStartFromSourceY(sourceY) {
		return Math.floor(sourceY * this.genogramHeight / this.sourceGenogramHeight)
	}

	getDestYEndFromSourceY(sourceY) {
		return Math.ceil(sourceY * this.genogramHeight / this.sourceGenogramHeight)
	}

	copyLegendIntoStripe(buffer, startRow, stripeHeight) {
		if (!this.legendImage) {
			return
		}

		let legendBottom = this.legendTop + this.legendImage.height
		let stripeBottom = startRow + stripeHeight
		let overlapTop = Math.max(this.legendTop, startRow)
		let overlapBottom = Math.min(legendBottom, stripeBottom)
		let copyHeight = Math.max(overlapBottom - overlapTop, 0)
		let sourceStartRow = overlapTop - this.legendTop
		let destStartRow = overlapTop - startRow

		for (let rowOffset = 0; rowOffset < copyHeight; rowOffset++) {
			let sourceRow = sourceStartRow + rowOffset
			let destRow = destStartRow + rowOffset
			let sourceStart = sourceRow * this.legendImage.width * RGBA_CHANNELS
			let sourceEnd = sourceStart + this.legendImage.width * RGBA_CHANNELS
			let destStart = ((destRow * this.finalWidth) + this.legendLeft) * RGBA_CHANNELS
			buffer.set(this.legendImage.data.subarray(sourceStart, sourceEnd), destStart)
		}
	}

	applyLegendBorderIntoStripe(buffer, startRow, stripeHeight) {
		if (!this.legendImage) {
			return
		}

		let rects = getLegendBorderRects({
			legendLeft: this.legendLeft,
			legendTop: this.legendTop,
			legendWidth: this.legendImage.width,
			legendHeight: this.legendImage.height,
		})

		for (let rect of rects) {
			this.fillRectIntoStripe(buffer, startRow, stripeHeight, rect, LEGEND_BORDER_RGBA)
		}
	}

	fillRectIntoStripe(buffer, startRow, stripeHeight, rect, rgba) {
		let stripeBottom = startRow + stripeHeight
		let visibleTop = Math.max(rect.top, startRow)
		let visibleBottom = Math.min(rect.top + rect.height, stripeBottom)
		let visibleLeft = Math.max(rect.left, 0)
		let visibleRight = Math.min(rect.left + rect.width, this.finalWidth)

		if (visibleTop >= visibleBottom || visibleLeft >= visibleRight) {
			return
		}

		for (let y = visibleTop; y < visibleBottom; y++) {
			let rowOffset = y - startRow

			for (let x = visibleLeft; x < visibleRight; x++) {
				this.setPixel(buffer, rowOffset, x, rgba)
			}
		}
	}

	applyDebugTileBorders(buffer, startRow, stripeHeight) {
		if (!this.showDebugTileBorders) {
			return
		}

		for (let tile of this.tiles) {
			this.drawTileBorder(buffer, startRow, stripeHeight, tile)
		}
	}

	drawTileBorder(buffer, startRow, stripeHeight, tile) {
		let left = this.padding + this.getTileLeft(tile)
		let top = this.padding + this.getTileTop(tile)
		let right = left + this.getTileWidth(tile) - 1
		let bottom = top + this.getTileHeight(tile) - 1
		let stripeBottom = startRow + stripeHeight - 1

		if (bottom < startRow || top > stripeBottom) {
			return
		}

		this.drawHorizontalBorder(buffer, startRow, stripeHeight, left, right, top)
		this.drawHorizontalBorder(buffer, startRow, stripeHeight, left, right, bottom)
		this.drawVerticalBorder(buffer, startRow, stripeHeight, left, top, bottom)
		this.drawVerticalBorder(buffer, startRow, stripeHeight, right, top, bottom)
	}

	drawHorizontalBorder(buffer, startRow, stripeHeight, left, right, y) {
		if (y < startRow || y >= startRow + stripeHeight) {
			return
		}

		let rowOffset = y - startRow

		for (let x = Math.max(left, 0); x <= Math.min(right, this.finalWidth - 1); x++) {
			this.setPixel(buffer, rowOffset, x, DEBUG_TILE_BORDER_RGBA)
		}
	}

	drawVerticalBorder(buffer, startRow, stripeHeight, x, top, bottom) {
		if (x < 0 || x >= this.finalWidth) {
			return
		}

		let visibleTop = Math.max(top, startRow)
		let visibleBottom = Math.min(bottom, startRow + stripeHeight - 1)

		for (let y = visibleTop; y <= visibleBottom; y++) {
			let rowOffset = y - startRow
			this.setPixel(buffer, rowOffset, x, DEBUG_TILE_BORDER_RGBA)
		}
	}

	setPixel(buffer, rowOffset, x, rgba) {
		let offset = ((rowOffset * this.finalWidth) + x) * RGBA_CHANNELS
		buffer[offset] = rgba[0]
		buffer[offset + 1] = rgba[1]
		buffer[offset + 2] = rgba[2]
		buffer[offset + 3] = rgba[3]
	}

	applyWatermarkToStripe(buffer, startRow, stripeHeight) {
		if (!this.showWatermark || !this.watermarkPattern) {
			return
		}

		for (let rowOffset = 0; rowOffset < stripeHeight; rowOffset++) {
			let globalRow = startRow + rowOffset
			let patternRow = globalRow % this.watermarkPattern.height
			this.applyWatermarkRow(buffer, rowOffset, patternRow)
		}
	}

	applyWatermarkRow(buffer, rowOffset, patternRow) {
		let rowStart = rowOffset * this.finalWidth * RGBA_CHANNELS

		for (let x = 0; x < this.finalWidth; x++) {
			let patternX = x % this.watermarkPattern.width
			let patternOffset = (patternRow * this.watermarkPattern.width + patternX) * RGBA_CHANNELS
			let alpha = this.watermarkPattern.data[patternOffset + 3]

			if (alpha > 0) {
				let destOffset = rowStart + x * RGBA_CHANNELS
				this.blendPixel(buffer, destOffset, patternOffset, alpha)
			}
		}
	}

	blendPixel(buffer, destOffset, patternOffset, alpha) {
		let alphaRatio = alpha / 255
		let inverseAlphaRatio = 1 - alphaRatio

		buffer[destOffset] = Math.round(this.watermarkPattern.data[patternOffset] * alphaRatio + buffer[destOffset] * inverseAlphaRatio)
		buffer[destOffset + 1] = Math.round(this.watermarkPattern.data[patternOffset + 1] * alphaRatio + buffer[destOffset + 1] * inverseAlphaRatio)
		buffer[destOffset + 2] = Math.round(this.watermarkPattern.data[patternOffset + 2] * alphaRatio + buffer[destOffset + 2] * inverseAlphaRatio)
		buffer[destOffset + 3] = 255
	}
}
